/**
 * Aceleres: identidade do PARTICIPANTE de um grupo do WhatsApp.
 *
 * Numa conversa de grupo o Chatwoot tem um contato só — o grupo — e portanto
 * todas as mensagens recebidas compartilham o mesmo `sender.id`. Sem uma
 * identidade por participante, o painel tratava o grupo inteiro como uma
 * pessoa: as bolhas de gente diferente entravam no mesmo bloco agrupado e o
 * único vestígio de quem falou era um prefixo cravado no texto da mensagem.
 *
 * Quem de fato enviou sempre esteve em `content_attributes` — este módulo é o
 * lugar único que lê esses campos, para que agrupamento (MessageList) e
 * apresentação (useDisplaySenderName) nunca discordem sobre quem é quem.
 *
 * As três ingestões existentes gravam o participante com nomes diferentes, e o
 * payload chega ora em camelCase (serializer do Chatwoot) ora em snake_case
 * (gravado direto pela ponte WAME) — daí a leitura tolerante em cada campo.
 */

const firstPresent = (...values) => {
  const found = values.find(
    value =>
      value !== null && value !== undefined && String(value).trim() !== ''
  );
  return found === undefined ? null : String(found).trim();
};

const attributeOf = (contentAttributes, camel, snake) =>
  firstPresent(contentAttributes?.[camel], contentAttributes?.[snake]);

// Paleta compartilhada pelos nomes dos participantes.
//
// Aceleres 2026-09-14: so `!important` nao bastava. O aceleres-overrides.css
// pinta o nome das bolhas de midia com `color: #000 !important` em seletores
// como `.message-bubble-container :is([data-bubble-name=...]) > .acl-media-sender`
// (0,3,0 ou mais), que venciam o utilitario (0,1,0): imagem, GIF, video,
// sticker e galeria saiam sempre pretos. O `:not(#acl)` soma especificidade
// de id (1,1,0) e ganha de todas essas regras sem mexer em cada uma.
// As classes ficam escritas por extenso para o Tailwind as encontrar.
const PARTICIPANT_COLOR_CLASSES = Object.freeze([
  '[&:not(#acl)]:!text-[#e05d32]',
  '[&:not(#acl)]:!text-[#ba33a3]',
  '[&:not(#acl)]:!text-[#1f8f78]',
  '[&:not(#acl)]:!text-[#4e63b6]',
  '[&:not(#acl)]:!text-[#a56b16]',
  '[&:not(#acl)]:!text-[#b54e63]',
  '[&:not(#acl)]:!text-[#0a7cae]',
  '[&:not(#acl)]:!text-[#7c4dcc]',
  '[&:not(#acl)]:!text-[#3b8526]',
  '[&:not(#acl)]:!text-[#c0392b]',
  '[&:not(#acl)]:!text-[#d0417e]',
  '[&:not(#acl)]:!text-[#00838f]',
]);

// Colisao entre membros do roster e resolvida em rosterParticipantColor; o
// hash so precisa ser deterministico.
const hashKey = key =>
  Array.from(key).reduce(
    (acc, char) => (acc * 131 + char.codePointAt(0)) % 2147483647,
    7
  );

/**
 * Cor deterministica do participante de um grupo.
 *
 * O hash usa a identidade estavel da pessoa (preferencialmente o JID sem o
 * sufixo do aparelho), portanto nao muda entre mensagens nem renderizacoes.
 *
 * @param {string|number|null} participantId identificador estavel
 * @returns {string} classe Tailwind da cor, ou vazio sem identificador
 */
export function getParticipantColor(participantId) {
  const key = String(participantId ?? '').trim();
  if (!key) return '';

  return PARTICIPANT_COLOR_CLASSES[
    hashKey(key) % PARTICIPANT_COLOR_CLASSES.length
  ];
}

const bareId = value =>
  String(value || '')
    .split('@')[0]
    .split(':')[0];

/**
 * Cor do participante resolvida contra o roster inteiro do grupo.
 *
 * Hash sozinho colide: com 6 cores, 4 dos 5 membros do grupo de teste
 * (conta 2569) caiam na mesma. Aqui cada membro, em ordem de id, fica com a
 * cor do seu hash e, se ela ja foi tomada, com a proxima livre. A ordem por
 * id (e nao a ordem em que a WAME devolve o roster) mantem a cor igual entre
 * sincronizacoes, e a entrada de um membro novo so desloca quem colidir com ele.
 *
 * @param {string} rosterId id da entrada do roster (ja sem dominio/aparelho)
 * @param {Array<object>} roster participantes do grupo
 * @returns {string} classe da cor, ou vazio quando o id nao esta no roster
 */
function rosterParticipantColor(rosterId, roster) {
  const ids = [
    ...new Set(
      roster
        .map(entry => bareId(firstPresent(entry?.id, entry?.phone_number)))
        .filter(Boolean)
    ),
  ].sort();
  if (!ids.includes(rosterId)) return '';

  const size = PARTICIPANT_COLOR_CLASSES.length;
  const taken = new Set();
  let found = '';
  ids.some(id => {
    let slot = hashKey(`roster:${id}`) % size;
    if (taken.size < size) {
      while (taken.has(slot)) slot = (slot + 1) % size;
    }
    taken.add(slot);
    if (id === rosterId) found = PARTICIPANT_COLOR_CLASSES[slot];
    return !!found;
  });
  return found;
}

/**
 * A mensagem pertence a uma conversa de grupo do WhatsApp?
 * @param {object} contentAttributes
 * @returns {boolean}
 */
export function isGroupMessage(contentAttributes) {
  const isGroup = contentAttributes?.isGroup ?? contentAttributes?.is_group;
  if (isGroup === true || isGroup === 'true') return true;

  // Ingestões antigas nem sempre gravaram `is_group`, mas o JID do grupo é
  // igualmente conclusivo: só thread de grupo termina em @g.us.
  const jid = attributeOf(
    contentAttributes,
    'wameRemoteJid',
    'wame_remote_jid'
  );
  const groupJid = attributeOf(
    contentAttributes,
    'whatsappGroupJid',
    'whatsapp_group_jid'
  );
  return !!(jid?.endsWith('@g.us') || groupJid?.endsWith('@g.us'));
}

/**
 * JID bruto do participante que enviou a mensagem.
 * @param {object} contentAttributes
 * @returns {string|null}
 */
export function groupParticipantJid(contentAttributes) {
  return (
    attributeOf(
      contentAttributes,
      'wameGroupParticipantJid',
      'wame_group_participant_jid'
    ) ??
    attributeOf(contentAttributes, 'whatsappSenderJid', 'whatsapp_sender_jid')
  );
}

/**
 * Chave estável do participante, usada para decidir se duas mensagens são da
 * MESMA pessoa.
 *
 * O sufixo `:<n>` de um JID é o identificador do APARELHO (`...935:49@...`), não
 * da pessoa: o mesmo participante mandando do celular e do WhatsApp Web produz
 * sufixos diferentes. Mantê-lo na chave quebraria o bloco visual a cada troca
 * de dispositivo. Já o domínio permanece: `@lid` e `@s.whatsapp.net` são
 * espaços de identificação distintos e o mesmo número em cada um não pode ser
 * fundido às cegas.
 *
 * @param {object} contentAttributes
 * @returns {string|null}
 */
export function groupParticipantKey(contentAttributes) {
  const jid = groupParticipantJid(contentAttributes);
  if (jid) {
    const [user, domain] = jid.split('@');
    const withoutDevice = String(user || '').split(':')[0];
    if (withoutDevice) {
      return domain ? `${withoutDevice}@${domain}` : withoutDevice;
    }
  }

  // Sem JID, o nome é a única âncora disponível. É mais fraco (dois
  // participantes homônimos colidem), mas ainda separa o caso comum.
  const name = attributeOf(
    contentAttributes,
    'wameGroupSenderName',
    'wame_group_sender_name'
  );
  return name ? `name:${name.toLowerCase()}` : null;
}

/**
 * Atributos do grupo guardados no CONTATO da conversa.
 *
 * A ponte WAME grava `is_whatsapp_group` e o roster em `custom_attributes`,
 * mas parte da UI procurava só em `additional_attributes` — e, encontrando um
 * hash vazio, concluía que a conversa não era de grupo. Ler os dois lugares
 * aqui evita que a resposta dependa de qual campo a ingestão usou.
 *
 * @param {object} conversation
 * @returns {object} atributos onde o marcador de grupo pode estar
 */
export function conversationGroupAttributes(conversation) {
  if (!conversation) return {};

  const sources = [
    conversation.meta?.sender?.custom_attributes,
    conversation.meta?.sender?.additional_attributes,
    conversation.meta?.contact?.custom_attributes,
    conversation.meta?.contact?.additional_attributes,
    conversation.custom_attributes,
    conversation.additional_attributes,
  ];

  return sources.reduce(
    (merged, source) =>
      source && typeof source === 'object' ? { ...source, ...merged } : merged,
    {}
  );
}

/**
 * A CONVERSA é um grupo do WhatsApp?
 * @param {object} conversation
 * @returns {boolean}
 */
export function isGroupConversation(conversation) {
  const attributes = conversationGroupAttributes(conversation);
  const flag = attributes.is_whatsapp_group ?? attributes.isWhatsappGroup;
  if (flag === true || flag === 'true') return true;

  const jid = String(attributes.remote_jid ?? attributes.remoteJid ?? '');
  return jid.endsWith('@g.us');
}

/**
 * Roster de participantes que a ponte WAME sincroniza no contato do grupo.
 * @param {object} conversation
 * @returns {Array<object>}
 */
export function groupRoster(conversation) {
  const attributes = conversationGroupAttributes(conversation);
  const group = attributes.whatsapp_group ?? attributes.whatsappGroup;
  const participants = group?.participants;

  return Array.isArray(participants) ? participants : [];
}

const DIGITS_ONLY = /^\+?[\d\s()-]+$/;

const isHumanName = value =>
  !!value && !DIGITS_ONLY.test(value) && !value.startsWith('+');

/**
 * Formata um número brasileiro para leitura. Devolve o original quando não
 * reconhece o formato — nunca inventa dígito.
 */
const formatPhone = digits => {
  const clean = String(digits).replace(/\D/g, '');
  if (clean.length === 13 && clean.startsWith('55')) {
    return `+55 ${clean.slice(2, 4)} ${clean.slice(4, 9)}-${clean.slice(9)}`;
  }
  if (clean.length === 12 && clean.startsWith('55')) {
    return `+55 ${clean.slice(2, 4)} ${clean.slice(4, 8)}-${clean.slice(8)}`;
  }
  return null;
};

/**
 * Localiza o participante no roster do grupo.
 *
 * O casamento precisa ser por id E por telefone porque as duas pontas nomeiam
 * a mesma pessoa de formas diferentes: o roster lista todo mundo por `@lid`
 * (`30352567464091@lid`), enquanto a mensagem traz ora esse mesmo `@lid`, ora
 * o JID de telefone (`555196235388@s.whatsapp.net`). Comparar só o id perderia
 * exatamente os participantes que mandam pelo número.
 */
const findInRoster = (jid, roster) => {
  if (!jid || !roster.length) return null;

  const [user] = jid.split('@');
  const bare = String(user || '').split(':')[0];
  if (!bare) return null;

  return (
    roster.find(participant => {
      const id = String(participant?.id || '')
        .split('@')[0]
        .split(':')[0];
      return id && id === bare;
    }) ??
    roster.find(participant => {
      const phone = String(participant?.phone_number || '').replace(/\D/g, '');
      return phone && phone === bare;
    }) ??
    null
  );
};

/**
 * Identidade canonica usada exclusivamente para escolher a cor.
 *
 * O mesmo participante pode enviar ora por `@lid`, ora pelo JID do telefone.
 * Quando ambos resolvem para a mesma entrada do roster, usamos o id dessa
 * entrada para que a troca de formato nao troque tambem a cor do nome.
 *
 * @param {object} contentAttributes atributos da mensagem
 * @param {Array<object>} [roster] participantes sincronizados no contato
 * @returns {string|null}
 */
export function groupParticipantColorKey(contentAttributes, roster = []) {
  const entry = findInRoster(groupParticipantJid(contentAttributes), roster);
  const rosterId = firstPresent(entry?.id, entry?.phone_number);
  if (rosterId) {
    return `roster:${rosterId.split('@')[0].split(':')[0]}`;
  }

  return groupParticipantKey(contentAttributes);
}

/**
 * Classe de cor do participante que enviou a mensagem: pelo roster quando ele
 * esta la (sem colisao entre membros), senao pelo hash da chave estavel.
 *
 * @param {object} contentAttributes atributos da mensagem
 * @param {Array<object>} [roster] participantes sincronizados no contato
 * @returns {string} classe da cor, ou vazio sem identidade
 */
export function groupParticipantColor(contentAttributes, roster = []) {
  const entry = findInRoster(groupParticipantJid(contentAttributes), roster);
  const rosterId = bareId(firstPresent(entry?.id, entry?.phone_number));
  const fromRoster = rosterId ? rosterParticipantColor(rosterId, roster) : '';
  if (fromRoster) return fromRoster;

  return getParticipantColor(groupParticipantKey(contentAttributes));
}

/**
 * Nome a exibir para o participante.
 *
 * O `~` que o WhatsApp prefixa em nomes de perfil público ("~Ângelo") é ruído
 * de protocolo e sai daqui.
 *
 * O roster tem precedência sobre o `pushName` da mensagem por uma razão de
 * estabilidade: o `pushName` viaja mensagem a mensagem e falta em algumas
 * (mídia reenviada, participante com privacidade restrita). Se cada bolha
 * escolhesse por si, a MESMA pessoa apareceria como "Bruno" numa e como o
 * número cru na seguinte, e o bloco visual se quebraria no meio da fala.
 *
 * @param {object} contentAttributes  content_attributes da mensagem
 * @param {Array<object>} [roster]    participantes sincronizados no contato
 * @returns {string|null}
 */
export function groupParticipantName(contentAttributes, roster = []) {
  const raw =
    attributeOf(
      contentAttributes,
      'wameGroupSenderName',
      'wame_group_sender_name'
    ) ??
    firstPresent(
      contentAttributes?.aceleresGroupSender?.resolved,
      contentAttributes?.aceleres_group_sender?.resolved
    );

  const pushName = raw ? raw.replace(/^~\s*/, '').trim() : null;

  const jid = groupParticipantJid(contentAttributes);
  const entry = findInRoster(jid, roster);
  const rosterName = entry?.name
    ? String(entry.name).replace(/^~\s*/, '').trim()
    : null;

  if (isHumanName(rosterName)) return rosterName;
  if (isHumanName(pushName)) return pushName;

  // Ninguém tem nome de gente: exibimos o telefone. O roster é a única fonte
  // que sabe o número por trás de um `@lid` — que é um id interno do WhatsApp,
  // não um telefone, e apareceria como "89495005331680" sem essa tradução.
  const rosterPhone = String(entry?.phone_number || '').replace(/\D/g, '');
  if (rosterPhone) return formatPhone(rosterPhone) ?? `+${rosterPhone}`;

  const digits = pushName || (jid ? jid.split('@')[0].split(':')[0] : null);
  if (!digits) return null;

  if (jid?.endsWith('@lid')) return pushName || digits;

  return formatPhone(digits) ?? digits;
}

/**
 * Foto do participante que enviou a mensagem.
 *
 * A fonte é o roster, e não o `sender` da mensagem: em grupo o `sender` é o
 * contato do GRUPO, então usar o avatar dele carimbaria a foto do grupo em
 * cada fala, seja de quem for. O roster ganha `thumbnail` no serializer do
 * Chatwoot (Aceleres::GroupParticipantAvatars), que casa o participante com o
 * contato individual dele por telefone.
 *
 * Devolve string vazia quando não há foto — é o que faz o `Avatar.vue` cair
 * nas iniciais do nome, como em todo o resto do painel.
 *
 * Os nomes alternativos abaixo são os mesmos que a ficha de membros do grupo
 * já aceitava (GroupMembers.vue#participantAvatar); ler os dois lugares o
 * mesmo conjunto evita que a foto apareça numa tela e falte na outra.
 *
 * @param {object} contentAttributes  content_attributes da mensagem
 * @param {Array<object>} [roster]    participantes sincronizados no contato
 * @returns {string} URL da foto, ou '' quando não há
 */
export function groupParticipantAvatar(contentAttributes, roster = []) {
  const entry = findInRoster(groupParticipantJid(contentAttributes), roster);
  if (!entry) return '';

  return (
    firstPresent(
      entry.thumbnail,
      entry.avatar_url,
      entry.profile_pic_url,
      entry.picture,
      entry.image_url
    ) ?? ''
  );
}

/**
 * Prefixos de remetente cravados no corpo da mensagem por ingestões anteriores.
 *
 * O nome do participante passou a viver só em `content_attributes`, mas o
 * histórico já gravado continua com o prefixo dentro do texto. Removê-lo na
 * renderização faz mensagem antiga e nova ficarem idênticas na tela sem
 * reescrever o banco.
 *
 * Cada padrão exige o nome que a própria mensagem declara — nunca um "qualquer
 * coisa antes de dois-pontos" —, de modo que uma mensagem legítima como
 * "Combinado: amanhã às 9h" jamais é decapitada.
 *
 * @param {string} content
 * @param {object} contentAttributes
 * @param {boolean} [isKnownGroup] a conversa ja foi identificada como grupo
 * @returns {string} conteúdo sem o prefixo de remetente
 */
export function stripGroupSenderPrefix(
  content,
  contentAttributes,
  isKnownGroup = false
) {
  if (typeof content !== 'string' || !content) return content;
  if (!isKnownGroup && !isGroupMessage(contentAttributes)) return content;

  const declared = attributeOf(
    contentAttributes,
    'wameGroupSenderName',
    'wame_group_sender_name'
  );

  if (declared) {
    const escaped = declared.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Ponte WAME ("Nome:\n"), initializer do Chatwoot ("[Nome]\n") e a forma
    // já reescrita em negrito ("**Nome:**").
    const legacy = new RegExp(
      `^(?:\\*\\*${escaped}:\\*\\*|\\[${escaped}\\]|${escaped}:)\\s*\\n?`
    );
    if (legacy.test(content)) {
      return content.replace(legacy, '');
    }
  }

  // Formato cru do Evolution: "**+55 4584339084 - Deus E Bom:**". O padrão é
  // específico o bastante (negrito + número + hífen + dois-pontos) para não
  // colidir com texto escrito por gente.
  const evolution = /^\*\*\s*\+?\d[\d\s]*?\s*-\s*.+?\s*:\*\*\s*\n?/;
  if (evolution.test(content)) {
    return content.replace(evolution, '');
  }

  return content;
}
